import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';
import { SecondaryButton } from '@/components/ui/Buttons';
import { db } from '@/lib/db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { Shield, ShieldAlert, ArrowLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export function VisibilityPage() {
    const { id: athleteId } = useParams();
    const navigate = useNavigate();
    const [isPublic, setIsPublic] = useState(false);
    const [loading, setLoading] = useState(true);

    const athlete = useLiveQuery(() =>
        athleteId ? db.athletes.get(athleteId) : undefined
    );

    // Calculate age based on birth_year if it exists
    const currentYear = new Date().getFullYear();
    const age = athlete?.birth_year ? currentYear - athlete.birth_year : null;
    const isUnderage = age !== null && age < 13;

    useEffect(() => {
        const fetchVisibility = async () => {
            if (!athleteId) return;
            const { data } = await supabase
                .from('athlete_visibility')
                .select('*')
                .eq('athlete_id', athleteId)
                .single();

            if (data) {
                setIsPublic(data.is_public);
            }
            setLoading(false);
        };
        fetchVisibility();
    }, [athleteId]);

    const handleToggle = async () => {
        if (isUnderage || !athleteId) return;

        const newValue = !isPublic;
        setIsPublic(newValue);

        // Upsert to Supabase
        await supabase
            .from('athlete_visibility')
            .upsert({
                athlete_id: athleteId,
                is_public: newValue,
                public_level: newValue ? 'basic' : 'none',
                locked_for_youth: isUnderage
            });
    };

    if (!athlete || loading) return <PageShell title="Loading Privacy..."><div /></PageShell>;

    return (
        <PageShell title="Privacy Settings" subtitle={athlete.name}>
            <div className="space-y-6 animate-fade-in-up pb-8">

                <button
                    onClick={() => navigate('/athletes')}
                    className="flex items-center gap-2 text-text-muted hover:text-white transition-colors text-sm font-bold uppercase tracking-widest"
                >
                    <ArrowLeft size={16} /> Back to Athletes
                </button>

                {isUnderage ? (
                    <EliteCard className="p-6 border-red-500/20 bg-red-500/5">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                                <ShieldAlert size={24} />
                            </div>
                            <div>
                                <h3 className="text-white font-black text-lg">Youth Privacy Locked</h3>
                                <p className="text-[11px] text-red-400 uppercase tracking-widest font-bold">Under 13 restrictions active</p>
                            </div>
                        </div>
                        <p className="text-text-muted text-sm leading-relaxed">
                            Because {athlete.name} is under 13, their profile is completely private by default and cannot be made public. This is to comply with COPPA and protect youth athletes.
                        </p>
                    </EliteCard>
                ) : (
                    <EliteCard className="p-6 border-gold-500/20">
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center text-gold-500">
                                    <Shield size={24} />
                                </div>
                                <div>
                                    <h3 className="text-white font-black text-lg">Public Profile</h3>
                                    <p className="text-[11px] text-text-muted uppercase tracking-widest font-bold">Showcase achievements</p>
                                </div>
                            </div>

                            {/* Toggle Switch */}
                            <button
                                onClick={handleToggle}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPublic ? 'bg-gold-500' : 'bg-bg-surface'}`}
                                role="switch"
                                aria-checked={isPublic}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPublic ? 'translate-x-5' : 'translate-x-0'}`}
                                />
                            </button>
                        </div>

                        <p className="text-text-muted text-sm leading-relaxed mb-6">
                            When enabled, {athlete.name} will have a public page showing their name, experience level, Win/Loss record, and match method breakdown. Detailed notes and video analysis remain private.
                        </p>

                        {isPublic && (
                            <SecondaryButton
                                onClick={() => navigate(`/public/${athlete.id}`)}
                                className="w-full justify-center"
                            >
                                View Public Profile
                            </SecondaryButton>
                        )}
                    </EliteCard>
                )}
            </div>
        </PageShell>
    );
}
